import { getRouteApi } from '@tanstack/react-router';
import { useId, useMemo, useState } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { SemPermissao } from '@/componentes/SemPermissao';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarData, formatarDataHora, formatarInteiro, paraCampoDataHora } from '@/lib/formato';
import { combinaComTermo, primeiraMaiuscula } from '@/lib/texto';
import { perfilAPedir } from '@/lib/perfil-da-licenca';
import { useSessao } from '@/modulos/sessao/consultas';

import { ErroDaApi } from './api';
import { MensagemDeErro, NotaDeEscopo, Situacao } from './Avisos';
import { lugarDoFormulario } from './campos';
import { EsqueletoDoFormulario, FormularioDeCadastro } from './FormularioDeCadastro';
import { chaveDoRascunhoDoCadastro, esquecerRascunhoDoCadastro } from './rascunho-do-cadastro';
import {
  OPCOES_POR_PAGINA,
  PADRAO_POR_PAGINA,
  SITUACOES,
  TIPO_PADRAO,
  montarBuscaDeCadastros,
  type Situacao as SituacaoDoFiltro,
} from './busca';
import { useCadastro, useGravarCadastro, useTrilha } from './consultas';
import {
  CADASTROS,
  NOME_DA_ACAO,
  NOME_DO_CADASTRO,
  RECURSO_DO_CADASTRO,
  flexaoDe,
  formatarDocumento,
  rotuloDe,
  type LinhaDeCadastro,
  type TipoDeCadastro,
} from './tipos';

const rota = getRouteApi('/cadastros');

/**
 * Modulo 01, ferramenta 1 — "cadastra empresas, clientes, locais, contratos de
 * referencia, fornecedores e centros de custo".
 *
 * ## Uma tela para os seis, e nao seis telas
 *
 * Os seis cadastros tem a mesma forma: lista, formulario de coluna unica,
 * painel de detalhe e historico. O que muda sao as colunas e os campos, e os
 * dois moram em tabela (`colunasDe` aqui, `CAMPOS_DO_CADASTRO` em `campos.ts`).
 * Seis telas dariam seis lugares para a mesma regra divergir — e a primeira a
 * divergir seria o estado vazio, que e o que ninguem testa.
 *
 * ## O que esta tela NAO tem, de proposito
 *
 * **Nao tem faixa de indicadores.** O manual e direto: se ninguem pergunta
 * aquilo de manha, o indicador sai da tela. "Quantos clientes cadastrados" nao
 * e pergunta de ninguem; a contagem que importa — quantos a lista esta
 * mostrando — ja esta na barra de filtros, onde o manual a pede.
 *
 * **Nao tem botao de excluir.** O mecanismo e ARQUIVAR. Apagar um cliente
 * cascatearia contratos e obras, e reescrever o passado e o oposto da
 * ferramenta de auditoria deste mesmo modulo.
 *
 * **Nao tem aviso de demonstracao.** O que aparece aqui esta gravado no banco.
 */
export function PaginaCadastros() {
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();

  const tipo = busca.tipo ?? TIPO_PADRAO;
  const termo = busca.termo ?? '';
  const situacao = busca.situacao;
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;

  const nome = NOME_DO_CADASTRO[tipo];
  const flexao = flexaoDe(tipo);
  /* R01 (p.29): o TIPO define o lugar do formulario, e `lugarDoFormulario` o
     deriva do numero de campos. O `form` so existe na URL para o que abre em
     pagina — o validador o descarta para o resto. */
  const lugar = lugarDoFormulario(tipo);
  const formNaUrl = busca.form;
  const consulta = useCadastro(tipo);
  const gravar = useGravarCadastro(tipo);
  const idDoFormulario = useId();
  /* Cadastrar, editar e arquivar só para quem gere os cadastros (prova do
     marco, 07/10/2026): quem só lê via os botões, e só o envio recusava. */
  const sessao = useSessao();
  const podeGerir = (sessao.data?.permissoes ?? []).includes('cadastro.gerir');
  /* O histórico da linha é a trilha, e a trilha é de quem lê a auditoria:
     Compras cadastra fornecedor e não lê a trilha (robôs no QAS, 20/10/2026). */
  const leTrilha = (sessao.data?.permissoes ?? []).includes('auditoria.ler');

  const [selecionado, setSelecionado] = useState<string | null>(null);
  /** `null` = formulario fechado; `{ linha: null }` = cadastrando. */
  const [formulario, setFormulario] = useState<{ readonly linha: LinhaDeCadastro | null } | null>(null);

  const todos = useMemo(() => consulta.data ?? [], [consulta.data]);

  const filtrados = useMemo(
    () =>
      todos.filter(
        (linha) =>
          (situacao === undefined ? true : situacao === 'Em uso' ? linha.ativo : !linha.ativo) &&
          combinaComTermo(
            [
              linha.codigo,
              rotuloDe(linha),
              linha.cliente_nome ?? null,
              linha.centro_custo_nome ?? null,
              linha.filial_nome ?? null,
              linha.municipio ?? null,
            ],
            termo,
          ),
      ),
    [todos, situacao, termo],
  );

  const paginas = Math.max(Math.ceil(filtrados.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = filtrados.slice(inicio, inicio + porPagina);

  const aberto = filtrados.find((linha) => linha.id === selecionado) ?? null;

  function irPara(estado: {
    readonly tipo?: TipoDeCadastro;
    readonly termo?: string;
    readonly situacao?: SituacaoDoFiltro | undefined;
    readonly pagina?: number;
    readonly porPagina?: number;
    readonly form?: string | undefined;
    readonly substituir?: boolean;
  }) {
    void navegar({
      search: montarBuscaDeCadastros({
        tipo: estado.tipo ?? tipo,
        termo: estado.termo ?? termo,
        situacao: 'situacao' in estado ? estado.situacao : situacao,
        pagina: estado.pagina ?? 1,
        porPagina: estado.porPagina ?? porPagina,
        form: 'form' in estado ? estado.form : formNaUrl,
      }),
      ...(estado.substituir ? { replace: true } : {}),
    });
  }

  /**
   * Abrir e fechar o formulario, pelo lugar que a R01 deu ao tipo.
   *
   * As duas funcoes existem para a tela ter UM lugar que decide: tres botoes
   * abrem o formulario (o do cabecalho, o do estado vazio e o "Editar" do
   * painel), e com a decisao repetida em cada um o terceiro divergiria.
   */
  function abrir(linha: LinhaDeCadastro | null) {
    gravar.reset();
    if (lugar === 'pagina') {
      setSelecionado(linha?.id ?? null);
      irPara({ form: linha?.id ?? 'novo' });
      return;
    }
    if (linha === null) setSelecionado(null);
    setFormulario({ linha });
  }

  function fechar() {
    gravar.reset();
    if (lugar === 'pagina') {
      irPara({ form: undefined });
      return;
    }
    setFormulario(null);
  }

  function limparFiltros() {
    irPara({ termo: '', situacao: undefined, pagina: 1 });
  }

  const chips: readonly ChipFiltro[] = [
    ...(situacao
      ? [
          {
            id: 'situacao',
            rotulo: `Situação: ${situacao}`,
            aoRemover: () => {
              irPara({ situacao: undefined });
            },
          },
        ]
      : []),
    ...(termo
      ? [
          {
            id: 'termo',
            rotulo: `Busca: ${termo}`,
            aoRemover: () => {
              irPara({ termo: '' });
            },
          },
        ]
      : []),
  ];

  /* A PAGINA INTEIRA, no lugar da lista (R01, p.29).
     Nao e uma rota propria: o cadastro e o mesmo assunto, e uma rota separada
     duplicaria o `tipo`, o filtro e a paginacao em dois validadores. O que a
     pagina precisa e de ENDERECO, e o `form` da URL o da — recarregar volta ao
     formulario, e o botao de voltar do navegador o fecha.
     Em edicao a linha vem da lista ja carregada; enquanto ela nao chegou, a
     tela diz que esta carregando em vez de desenhar um formulario vazio que
     se preencheria sozinho um instante depois. */
  if (lugar === 'pagina' && formNaUrl !== undefined) {
    /* O formulário em página pelo endereço (`?form=`), sem a licença: o perfil
       a pedir no lugar do formulário que só o envio recusaria. */
    if (!podeGerir) {
      return (
        <div className="min-w-0 flex-1 overflow-auto">
          <div className="pagina">
            <CabecalhoPagina sobretitulo="Cadastros e governança" titulo={`Cadastrar ${nome.um}`} />
            {sessao.data ? <SemPermissao modulo="o cadastro da estrutura" perfil="Compras" /> : null}
          </div>
        </div>
      );
    }
    const emEdicao = formNaUrl === 'novo' ? null : (todos.find((linha) => linha.id === formNaUrl) ?? null);
    return (
      <FormularioEmPagina
        tipo={tipo}
        linha={emEdicao}
        carregando={formNaUrl !== 'novo' && emEdicao === null && consulta.isPending}
        naoAchada={formNaUrl !== 'novo' && emEdicao === null && !consulta.isPending}
        gravando={gravar.isPending}
        erro={gravar.error}
        aoCancelar={fechar}
        aoGravar={(campos) => {
          gravar.mutate(
            { id: emEdicao?.id ?? null, campos },
            {
              onSuccess: (gravada) => {
                /* Gravou: o rascunho daquele formulario sai (p.16). */
                esquecerRascunhoDoCadastro(chaveDoRascunhoDoCadastro(tipo, emEdicao?.id ?? null, sessao.data ?? null));
                setSelecionado(gravada.id);
                irPara({ form: undefined });
              },
            },
          );
        }}
      />
    );
  }

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Cadastros e governança"
            titulo="Estrutura e centros de custo"
            subtitulo="A base que operação, oficina e gestão compartilham"
            acoes={
              /* O laranja da tela leva ao formulario, no lugar que a R01 deu
                 ao tipo: janela para os curtos, pagina inteira para obras. Nos
                 dois casos o laranja que se alcanca depois e o de gravar. */
              podeGerir ? (
                <Botao
                  nivel="primario"
                  onClick={() => {
                    abrir(null);
                  }}
                >
                  {`Cadastrar ${nome.botao}`}
                </Botao>
              ) : undefined
            }
          />

          {/* A FRONTEIRA DA FERRAMENTA 1, escrita onde ela é lida.
              O "como funciona" do módulo 01 começa por "cadastra EMPRESAS,
              clientes, locais, contratos de referência, fornecedores e centros
              de custo" — e empresa não é um dos seis cadastros desta tela.
              Num sistema multiempresa isso é defensável (empresa nova nasce na
              administração da plataforma, acima das empresas), mas então é
              isso que a tela tem que dizer, na palavra de quem usa, em vez de
              deixar a ausência passar por esquecimento. */}
          <NotaDeEscopo
            titulo="Criar empresa não se faz por aqui"
            detalhe="Estes seis cadastros são da sua empresa. Empresa nova é aberta pela administração da plataforma."
          />

          <HistoriaNaMesa codigo="FLT-CAD-01" className="w-fit max-w-full">
            <Seletor
              rotulo="Cadastro"
              valor={tipo}
              opcoes={CADASTROS.map((chave) => ({ valor: chave, rotulo: NOME_DO_CADASTRO[chave].muitos }))}
              aoEscolher={(escolhido) => {
                setSelecionado(null);
                setFormulario(null);
                /* Trocar de cadastro FECHA o formulario: os campos do escolhido
                   nao sao os do que estava aberto, e `form: undefined` tira da
                   URL o endereco que o novo tipo talvez nem tenha. */
                irPara({ tipo: escolhido, pagina: 1, form: undefined });
              }}
            />
          </HistoriaNaMesa>

          <BarraFiltros
            chips={chips}
            /* "0 de 0" antes da lista chegar e um valor falso (p.32): vazio
               ate ela chegar. */
            contagem={
              consulta.isPending
                ? null
                : `${formatarInteiro(filtrados.length)} de ${formatarInteiro(todos.length)} — ${nome.muitos.toLowerCase()}`
            }
            aoLimpar={limparFiltros}
          >
            <div className="min-w-0 flex-1">
              <CampoTexto
                rotulo="Buscar"
                valor={termo}
                placeholder={DICA_DA_BUSCA[tipo]}
                atalho="ctrl-k"
                aplicado={Boolean(termo)}
                aoMudar={(novo) => {
                  irPara({ termo: novo, substituir: true });
                }}
              />
            </div>

            <CampoSelecao
              rotulo="Situação"
              valor={situacao ?? ''}
              rotuloVazio="Todas"
              aplicado={Boolean(situacao)}
              opcoes={SITUACOES.map((nomeDaSituacao) => ({ valor: nomeDaSituacao, rotulo: nomeDaSituacao }))}
              aoMudar={(novo) => {
                irPara({ situacao: novo === 'Em uso' || novo === 'Arquivado' ? novo : undefined });
              }}
            />
          </BarraFiltros>

          <div data-painel-origem>
            <Tabela
              rotulo={`${nome.muitos} ${flexao.cadastrados}`}
              colunas={colunasDe(tipo)}
              linhas={visiveis}
              chaveDaLinha={(linha) => linha.id}
              carregando={consulta.isPending}
              selecionada={selecionado}
              aoSelecionar={(linha) => {
                setSelecionado(linha.id);
              }}
              erro={
                consulta.isError ? (
                  <EstadoVazio
                    titulo={
                      consulta.error instanceof ErroDaApi && consulta.error.status === 401
                        ? 'Sua sessão não está aberta'
                        : `Não foi possível carregar ${nome.muitos.toLowerCase()}`
                    }
                    descricao={
                      consulta.error instanceof ErroDaApi
                        ? consulta.error.comoResolver
                        : 'A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário.'
                    }
                    acao={
                      <Botao
                        nivel="secundario"
                        onClick={() => {
                          void consulta.refetch();
                        }}
                      >
                        Carregar a lista
                      </Botao>
                    }
                  />
                ) : null
              }
              vazio={
                todos.length === 0 ? (
                  <EstadoVazio
                    titulo={`${flexao.nenhum} ${nome.um} ${flexao.cadastrado}`}
                    descricao={
                      podeGerir
                        ? `Cadastre ${flexao.primeiro} ${nome.um} para começar. ${flexao.ele} passa a aparecer nas listas de escolha das outras telas.`
                        : 'A lista se preenche quando o perfil Compras cadastrar. Peça a ele, ou ao administrador da sua empresa.'
                    }
                    acao={
                      podeGerir ? (
                        <Botao
                          nivel="secundario"
                          onClick={() => {
                            abrir(null);
                          }}
                        >
                          {`Cadastrar ${nome.botao}`}
                        </Botao>
                      ) : undefined
                    }
                  />
                ) : (
                  <EstadoVazio
                    titulo={`${flexao.nenhum} ${nome.um} com esses filtros`}
                    descricao="Troque a situação, apague a busca ou limpe os filtros para ver a lista inteira."
                    acao={
                      <Botao nivel="secundario" onClick={limparFiltros}>
                        Limpar filtros
                      </Botao>
                    }
                  />
                )
              }
              rodape={
                <RodapeTabela
                  primeiro={inicio + 1}
                  ultimo={inicio + visiveis.length}
                  total={filtrados.length}
                  pagina={paginaSegura}
                  paginas={paginas}
                  porPagina={porPagina}
                  opcoesPorPagina={OPCOES_POR_PAGINA}
                  aoMudarPagina={(nova) => {
                    irPara({ pagina: nova });
                  }}
                  aoMudarPorPagina={(novo) => {
                    irPara({ porPagina: novo, pagina: 1 });
                  }}
                />
              }
            />
          </div>
        </div>
      </div>

      {/* Com a janela do formulario aberta, o painel sai: duas sobreposicoes
          empilhadas (a gaveta abaixo de 1280 e a janela) a R01 proibe. A linha
          continua marcada, e o painel volta ao fechar a janela. */}
      {aberto && !formulario ? (
        <Detalhe
          key={aberto.id}
          tipo={tipo}
          linha={aberto}
          podeGerir={podeGerir}
          leTrilha={leTrilha}
          aoFechar={() => {
            setSelecionado(null);
          }}
          aoEditar={() => {
            abrir(aberto);
          }}
        />
      ) : null}

      <Janela
        /* O titulo da janela leva o nome inteiro: titulo nao tem o limite de
           tres palavras do botao. */
        titulo={
          formulario === null
            ? ''
            : formulario.linha === null
              ? `Cadastrar ${nome.um}`
              : `Editar ${nome.um} ${rotuloDe(formulario.linha)}`
        }
        aberta={formulario !== null && podeGerir}
        aoFechar={fechar}
        /* Rodape fixo da janela (R01, p.29): Cancelar a esquerda, o primario a
           direita. O de gravar envia o formulario do corpo pelo `form`. */
        acoes={
          <>
            <Botao nivel="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao nivel="primario" type="submit" form={idDoFormulario} disabled={gravar.isPending}>
              {formulario?.linha ? `Salvar ${nome.botao}` : `Cadastrar ${nome.botao}`}
            </Botao>
          </>
        }
      >
        {formulario && podeGerir ? (
          <FormularioDeCadastro
            key={`${tipo}-${formulario.linha?.id ?? 'novo'}`}
            id={idDoFormulario}
            tipo={tipo}
            linha={formulario.linha}
            erro={gravar.error}
            aoGravar={(campos) => {
              gravar.mutate(
                { id: formulario.linha?.id ?? null, campos },
                {
                  onSuccess: (gravada) => {
                    esquecerRascunhoDoCadastro(chaveDoRascunhoDoCadastro(tipo, formulario.linha?.id ?? null, sessao.data ?? null));
                    setFormulario(null);
                    setSelecionado(gravada.id);
                  },
                },
              );
            }}
          />
        ) : null}
      </Janela>
    </>
  );
}

/**
 * O painel de detalhe (p.20): identidade, dados-chave, abas, acoes.
 *
 * A aba "Histórico" e a ferramenta 3 do modulo dentro da ferramenta 1: ela le a
 * trilha DAQUELA linha, que o trigger escreveu na mesma transacao da mudanca.
 * Ninguem a alimenta; ela existe porque o banco a escreve.
 */
function Detalhe({
  tipo,
  linha,
  podeGerir,
  leTrilha,
  aoFechar,
  aoEditar,
}: {
  readonly tipo: TipoDeCadastro;
  readonly linha: LinhaDeCadastro;
  /** `cadastro.gerir`: sem ela, o painel e so leitura — sem Editar nem Arquivar. */
  readonly podeGerir: boolean;
  /** `auditoria.ler`: sem ela, a aba Histórico nao pede a trilha e diz o perfil a pedir. */
  readonly leTrilha: boolean;
  readonly aoFechar: () => void;
  readonly aoEditar: () => void;
}) {
  const nome = NOME_DO_CADASTRO[tipo];
  const flexao = flexaoDe(tipo);
  const arquivar = useGravarCadastro(tipo);
  const historico = useTrilha({ recurso: RECURSO_DO_CADASTRO[tipo], recursoId: linha.id }, leTrilha);

  /* Identidade (p.19): o codigo pequeno em monoespacada, o NOME como titulo —
     e o nome que identifica — e, embaixo, de que cadastro ele e. Patio e
     oficina moram no cadastro de obras, e nao sao obra: ali o subtitulo e o
     da coluna, "Obra ou local". */
  return (
    <PainelDetalhe
      codigo={linha.codigo}
      titulo={rotuloDe(linha)}
      subtitulo={tipo === 'obras' ? 'Obra ou local' : primeiraMaiuscula(nome.um)}
      marcadores={<Situacao ativo={linha.ativo} />}
      dadosChave={
        <>
          {linha.documento ? (
            <DadoChave rotulo="CPF ou CNPJ">{formatarDocumento(linha.documento)}</DadoChave>
          ) : null}
          {linha.tipo ? <DadoChave rotulo="Tipo">{NOME_DO_TIPO_DE_OBRA[linha.tipo] ?? linha.tipo}</DadoChave> : null}
          {linha.cliente_nome ? <DadoChave rotulo="Cliente">{linha.cliente_nome}</DadoChave> : null}
          {linha.centro_custo_nome ? (
            <DadoChave rotulo="Centro de custo">{linha.centro_custo_nome}</DadoChave>
          ) : null}
          {linha.filial_nome ? <DadoChave rotulo="Filial">{linha.filial_nome}</DadoChave> : null}
          {linha.municipio ? (
            <DadoChave rotulo="Município">{`${linha.municipio}${linha.uf ? ` · ${linha.uf}` : ''}`}</DadoChave>
          ) : null}
          {linha.inicio ? (
            <DadoChave rotulo="Vigência">
              {`${formatarData(new Date(linha.inicio))} até `}
              {linha.fim ? (
                <span className={vigenciaVencida(linha.fim) ? 'text-data-vencida' : undefined}>
                  {formatarData(new Date(linha.fim))}
                </span>
              ) : (
                'sem data de término'
              )}
            </DadoChave>
          ) : null}
          <DadoChave rotulo="Cadastrado">{formatarDataHora(new Date(linha.criado_em))}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'historico',
          rotulo: 'Histórico',
          historia: 'ABA-CAD-05',
          conteudo: !leTrilha ? (
            <SemPermissao titulo="Você não vê o histórico" modulo="a trilha de auditoria" perfil={perfilAPedir('auditoria.ler')} />
          ) : historico.isPending ? (
            /* Carregando (p.32) na forma do registro: a ação e a data na
               primeira linha, o autor na segunda. */
            <Carregando oQue="Carregando o histórico" className="flex flex-col gap-3">
              {['w-24', 'w-32', 'w-20'].map((largura, i) => (
                <div key={i} className="flex flex-col gap-1 border-b border-borda-sutil pb-2">
                  <span className="flex items-center justify-between gap-4 py-1">
                    <Barra className={`h-3 ${largura}`} />
                    <Barra className="h-3 w-28" />
                  </span>
                  <Barra className={`my-1 h-3 ${i % 2 === 0 ? 'w-40' : 'w-32'}`} />
                </div>
              ))}
            </Carregando>
          ) : historico.isError ? (
            <MensagemDeErro erro={historico.error} />
          ) : (historico.data?.itens.length ?? 0) === 0 ? (
            <EstadoVazio
              titulo="Sem alterações registradas"
              descricao="Toda mudança neste cadastro entra aqui sozinha, com autor, data e motivo."
            />
          ) : (
            <ul className="flex flex-col gap-3">
              {(historico.data?.itens ?? []).map((registro) => (
                <li key={registro.id} className="flex flex-col gap-1 border-b border-borda-sutil pb-2">
                  <span className="flex items-baseline justify-between gap-4">
                    <span className="txt-corpo text-tinta">
                      {NOME_DA_ACAO[registro.acao] ?? registro.acao}
                    </span>
                    <span className="txt-apoio text-tinta-secundaria">
                      {formatarDataHora(new Date(registro.ocorrido_em))}
                    </span>
                  </span>
                  <span className="txt-apoio text-tinta-secundaria">
                    {registro.autor ?? 'Autor não encontrado no cadastro de pessoas'}
                  </span>
                  {registro.motivo ? (
                    <span className="txt-apoio text-tinta-apagada">{registro.motivo}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ),
        },
      ]}
      notaDasAcoes={`Arquivar tira ${flexao.artigo} ${nome.um} das listas de escolha e mantém tudo o que já foi lançado ${flexao.comEle}. Não há exclusão: apagar levaria junto contratos e obras.`}
      acoes={podeGerir ? [
        <Botao key="editar" nivel="secundario" onClick={aoEditar}>
          {`Editar ${nome.botao}`}
        </Botao>,
        linha.ativo ? (
          <BotaoDestrutivo
            key="arquivar"
            rotulo={`Arquivar ${nome.botao}`}
            pergunta={`Arquivar ${nome.um} ${linha.codigo}?`}
            consequencia={`${flexao.ele} sai das listas de escolha das outras telas. O que já foi lançado ${flexao.comEle} continua no lugar, e o histórico fica.`}
            rotuloConfirmar={`Arquivar ${nome.botao}`}
            rotuloCancelar="Manter como está"
            pedirMotivo="cadastro.desativar"
            aoConfirmar={(motivo) => {
              arquivar.mutate({ id: linha.id, campos: { ativo: false, motivo } });
            }}
          />
        ) : (
          <Botao
            key="reabrir"
            nivel="secundario"
            onClick={() => {
              arquivar.mutate({ id: linha.id, campos: { ativo: true } });
            }}
          >
            {`Reabrir ${nome.botao}`}
          </Botao>
        ),
      ] : []}
      aoFechar={aoFechar}
    />
  );
}

const NOME_DO_TIPO_DE_OBRA: Record<string, string> = {
  obra: 'Obra de cliente',
  patio: 'Pátio próprio',
  oficina: 'Oficina',
};

/**
 * O que a busca procura, na palavra de cada cadastro (p.16: a busca diz o que
 * procura). Curta de proposito: a frase unica de antes ("Código, nome,
 * cliente, centro de custo ou município") pedia 323 px num campo de 298 a
 * 390 px e saia cortada; em caixa alta, pior.
 */
const DICA_DA_BUSCA: Record<TipoDeCadastro, string> = {
  filiais: 'Código, nome ou município',
  clientes: 'Código ou nome',
  fornecedores: 'Código ou nome',
  'centros-de-custo': 'Código, nome ou filial',
  obras: 'Código, nome, cliente ou município',
  contratos: 'Código, descrição ou cliente',
};

/**
 * As colunas de cada cadastro. Nenhuma lista passa de sete — o teto do manual e
 * oito (p.18), e a folga existe para a coluna seguinte nao chegar num limite.
 *
 * A primeira coluna e sempre o codigo, em monoespacada: e a identidade da linha
 * e o que se compara de relance numa lista longa.
 *
 * SECUNDARIA (R01, p.28): a coluna que sai em M (1024–1279) e com o quadro
 * estreito, e cujo dado continua nos dados-chave do painel. Sem nenhuma
 * marcada, as sete colunas de obras ficavam todas a 1024, e a que saia cortada
 * na borda era justamente a Situacao, que e chave.
 *
 * Nome e texto livre vao a ate duas linhas (`TextoDaCelula`), com o texto
 * inteiro no `title` e no painel; codigo, documento e data nao quebram.
 */
function colunasDe(tipo: TipoDeCadastro): readonly Coluna<LinhaDeCadastro>[] {
  const codigo: Coluna<LinhaDeCadastro> = {
    chave: 'codigo',
    rotulo: 'Código',
    celula: (linha) => <span className="codigo text-tinta">{linha.codigo}</span>,
  };

  const situacao: Coluna<LinhaDeCadastro> = {
    chave: 'situacao',
    rotulo: 'Situação',
    alinhamento: 'status',
    celula: (linha) => <Situacao ativo={linha.ativo} />,
  };

  const nome = (rotulo: string): Coluna<LinhaDeCadastro> => ({
    chave: 'nome',
    rotulo,
    celula: (linha) => <TextoDaCelula texto={rotuloDe(linha)} />,
  });

  const semValor = (valor: string) => <span className="text-tinta-secundaria">{valor}</span>;

  const texto = (valor: string | null | undefined, vazio: string) =>
    valor ? <TextoDaCelula texto={valor} /> : semValor(vazio);

  switch (tipo) {
    case 'filiais':
      return [
        codigo,
        nome('Filial'),
        { chave: 'municipio', rotulo: 'Município', celula: (l) => texto(l.municipio, 'Não informado') },
        { chave: 'uf', rotulo: 'UF', quebra: 'nunca', celula: (l) => l.uf ?? semValor('—') },
        situacao,
      ];

    case 'clientes':
    case 'fornecedores':
      return [
        codigo,
        nome(tipo === 'clientes' ? 'Cliente' : 'Fornecedor'),
        {
          chave: 'documento',
          rotulo: 'CPF ou CNPJ',
          quebra: 'nunca',
          celula: (l) => (l.documento ? formatarDocumento(l.documento) : semValor('Não informado')),
        },
        situacao,
      ];

    case 'centros-de-custo':
      return [
        codigo,
        nome('Centro de custo'),
        { chave: 'filial', rotulo: 'Filial', celula: (l) => texto(l.filial_nome, 'Sem filial') },
        situacao,
      ];

    case 'obras':
      /* Tipo, centro de custo e municipio sao secundarios: os tres estao nos
         dados-chave do painel. Ficam codigo, nome, cliente e situacao. */
      return [
        codigo,
        nome('Obra ou local'),
        {
          chave: 'tipo',
          rotulo: 'Tipo',
          prioridade: 'secundaria',
          celula: (l) => (l.tipo ? (NOME_DO_TIPO_DE_OBRA[l.tipo] ?? l.tipo) : ''),
        },
        { chave: 'cliente', rotulo: 'Cliente', celula: (l) => texto(l.cliente_nome, 'Da própria empresa') },
        {
          chave: 'centro',
          rotulo: 'Centro de custo',
          historia: 'COL-CAD-02',
          prioridade: 'secundaria',
          celula: (l) => texto(l.centro_custo_nome, 'Sem centro de custo'),
        },
        {
          chave: 'municipio',
          rotulo: 'Município',
          prioridade: 'secundaria',
          celula: (l) => texto(l.municipio, 'Não informado'),
        },
        situacao,
      ];

    case 'contratos':
      /* O inicio e secundario: a vigencia inteira esta no painel, e o FIM —
         que e o que vence e fica vermelho (R02) — continua na lista. */
      return [
        codigo,
        nome('Descrição'),
        { chave: 'cliente', rotulo: 'Cliente', celula: (l) => texto(l.cliente_nome, 'Sem cliente') },
        {
          chave: 'inicio',
          rotulo: 'Início',
          prioridade: 'secundaria',
          quebra: 'nunca',
          celula: (l) => (l.inicio ? formatarData(new Date(l.inicio)) : semValor('—')),
        },
        {
          chave: 'fim',
          rotulo: 'Fim',
          /* R02: data vencida em vermelho. Só o vencido — "próximo" pediria
             um prazo de aviso que o módulo 01 não define. */
          celula: (l) =>
            l.fim ? (
              <span className={vigenciaVencida(l.fim) ? 'text-data-vencida' : undefined}>
                {formatarData(new Date(l.fim))}
              </span>
            ) : (
              semValor('Vigência aberta')
            ),
        },
        situacao,
      ];
  }
}

/**
 * O fim da vigência já passou? Compara o dia em hora local, os dois lados pelo
 * mesmo caminho da data que a tela mostra (`new Date(fim)`): o `fim` chega em
 * UTC (`tstzrange`), e cortar a string daria o dia de Greenwich, não o exibido.
 */
function vigenciaVencida(fim: string): boolean {
  return paraCampoDataHora(new Date(fim)).slice(0, 10) < paraCampoDataHora(new Date()).slice(0, 10);
}

/**
 * O formulario de pagina inteira (R01, p.29).
 *
 * ## Por que obras nao cabe numa janela
 *
 * A faixa da pagina e a unica da p.29 com numero: "mais de 10 campos ou varias
 * secoes". Obras tem 12 campos em tres secoes, e o modal da R01 e travado em
 * 480–640 px — nessa largura a grade cai para UMA coluna, e os 12 campos
 * viram uma fita de rolagem dentro de uma caixa de 85% da altura da tela, com
 * a barra de acoes escondendo o fim. Era o aperto que a faixa da pagina existe
 * para evitar. Contratos tem 6 campos e continua na janela, pela mesma regra.
 *
 * ## O que a pagina tem que a janela nao tinha
 *
 * A BARRA DE ACOES E FIXA e o formulario rola POR BAIXO dela (R01, p.28): com
 * 12 campos, uma barra que rola com o conteudo manda procurar o "Cadastrar" no
 * fim da fita. Ela repete a ordem da p.29 — voltar a esquerda, a acao a
 * direita — para a mao nao ter que reaprender o lugar ao sair da janela.
 *
 * NAO ha indice lateral de secoes. A R01 o pede acima de TRES secoes a partir
 * de 1280 px, e obras tem exatamente tres: construir um indice de tres itens
 * seria inventar componente que o manual nao pediu.
 */
function FormularioEmPagina({
  tipo,
  linha,
  carregando,
  naoAchada,
  gravando,
  erro,
  aoCancelar,
  aoGravar,
}: {
  readonly tipo: TipoDeCadastro;
  readonly linha: LinhaDeCadastro | null;
  readonly carregando: boolean;
  readonly naoAchada: boolean;
  readonly gravando: boolean;
  readonly erro: unknown;
  readonly aoCancelar: () => void;
  readonly aoGravar: (campos: Record<string, unknown>) => void;
}) {
  const nome = NOME_DO_CADASTRO[tipo];
  const flexao = flexaoDe(tipo);
  const idDoFormulario = useId();
  /* Enquanto carrega, e edicao (o `novo` nao carrega nada): o cabecalho ja diz
     isso, e nao "Cadastrar" ate o registro chegar. */
  const cadastrando = linha === null && !carregando;

  if (naoAchada) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <EstadoVazio
            titulo={`${primeiraMaiuscula(nome.um)} não encontrad${nome.genero === 'f' ? 'a' : 'o'}`}
            descricao={`O endereço aponta para ${nome.um} que não está na lista. Pode ter sido arquivad${nome.genero === 'f' ? 'a' : 'o'} por outra pessoa; volte à lista para ver.`}
            acao={
              <Botao nivel="secundario" onClick={aoCancelar}>
                {`Voltar para ${nome.muitos.toLowerCase()}`}
              </Botao>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo={nome.muitos}
            titulo={
              linha === null
                ? cadastrando
                  ? `Cadastrar ${nome.um}`
                  : `Editar ${nome.um}`
                : `Editar ${nome.um} ${rotuloDe(linha)}`
            }
            subtitulo={
              cadastrando
                ? 'Os campos marcados como opcionais podem ficar em branco'
                : 'A mudança entra na trilha com autor, data e motivo'
            }
          />

          {carregando ? (
            <EsqueletoDoFormulario tipo={tipo} oQue={`Carregando ${flexao.artigo} ${nome.um}`} />
          ) : (
            <FormularioDeCadastro
              key={`${tipo}-${linha?.id ?? 'novo'}`}
              id={idDoFormulario}
              tipo={tipo}
              linha={linha}
              erro={erro}
              aoGravar={aoGravar}
            />
          )}
        </div>
      </div>

      {/* A barra fixa: fora do que rola, na ordem da p.29. */}
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-borda-sutil bg-rebaixada px-6 py-4">
        <Botao nivel="secundario" onClick={aoCancelar}>
          Cancelar
        </Botao>
        <Botao nivel="primario" type="submit" form={idDoFormulario} disabled={carregando || gravando}>
          {cadastrando ? `Cadastrar ${nome.botao}` : `Salvar ${nome.botao}`}
        </Botao>
      </div>
    </div>
  );
}
